import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, BedDouble } from 'lucide-react';
import { formatCATDateTime } from '../utils/timezone';
import { deptLabel } from './workflow/VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—');

// Printable discharge letter for an admitted-and-discharged visit.
export default function PrintDischargeLetter() {
  const { visitId } = useParams();
  const [visit, setVisit] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    axios.get(`${API}/visits/${visitId}`, { withCredentials: true })
      .then((r) => { setVisit(r.data); setState('ok'); })
      .catch((e) => setState(e.response?.status === 401 ? 'login' : 'error'));
  }, [visitId]);

  if (state === 'login') { window.location.href = '/login'; return null; }
  if (state === 'loading') return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading…</div>;
  if (state === 'error' || !visit) return <div className="flex items-center justify-center min-h-screen text-gray-600">Visit not found</div>;

  const adm = visit.admission || {};
  if (!adm.number) return <div className="flex items-center justify-center min-h-screen text-gray-600" data-testid="discharge-letter-none">This patient was not admitted — no discharge letter available.</div>;
  const c = visit.consultation || {};
  const takeHome = (visit.prescriptions || []).filter((p) => p.take_home && ['pending', 'dispensed'].includes(p.status));
  const stayMeds = (visit.prescriptions || []).filter((p) => !p.take_home && p.status === 'dispensed');
  const tests = ['lab_orders', 'rad_orders', 'dental_orders'].flatMap((k) => (visit[k] || []).filter((o) => o.status === 'completed'));
  const doctor = adm.discharged_by_name || c.doctor_name || adm.discharged_by || c.reviewed_by || c.consultant || '—';
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[180px_1fr] gap-2 text-sm py-1 border-b border-gray-100"><span className="text-gray-600">{label}</span><span className="text-gray-900 whitespace-pre-line [overflow-wrap:anywhere]">{children || '—'}</span></div>
  );

  return (
    <>
      <style>{`@media print { .no-break { break-inside: avoid; } @page { margin: 1.2cm; } }`}</style>
      <div className="min-h-screen bg-gray-50 p-8 print:p-0 print:bg-white">
        <div className="max-w-3xl mx-auto mb-4 print:hidden flex justify-between items-center bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-600"><strong>Discharge Letter</strong> — give a copy to the patient</div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700" data-testid="print-discharge-button"><Printer className="w-4 h-4 mr-2" /> Print</Button>
            <Button variant="outline" onClick={() => window.close()}>Close</Button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg print:shadow-none p-10 print:p-6" data-testid="discharge-letter">
          <div className="flex items-start justify-between border-b-2 border-gray-700 pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><BedDouble className="w-6 h-6" /> Discharge Letter</h1>
              <p className="text-lg text-gray-700 font-medium">{visit.hospital_name}</p>
              <p className="text-xs text-gray-500">Acacia Medical Insurance — inpatient care record</p>
            </div>
            <div className="text-right text-sm text-gray-700">
              <p><span className="text-gray-500">Admission #</span> <span className="font-bold font-mono" data-testid="discharge-letter-number">{adm.number}</span></p>
              <p><span className="text-gray-500">Visit</span> {visit.visit_id}</p>
              <p><span className="text-gray-500">Issued</span> {fmtDate(adm.discharged_at)}</p>
            </div>
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Patient</h2>
            <Row label="Name"><strong data-testid="discharge-letter-patient">{visit.patient_name}</strong></Row>
            <Row label="Serial / Family">{visit.patient_serial_number} • {visit.family_id}</Row>
            <Row label="Sex / Date of birth">{visit.patient_sex || '—'} • {visit.patient_dob || '—'}</Row>
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Admission</h2>
            <Row label="Admitted">{formatCATDateTime(adm.approved_at)}{adm.ward ? ` — ${adm.ward}` : ''}</Row>
            <Row label="Discharged">{formatCATDateTime(adm.discharged_at)}{adm.bed_days ? ` (${adm.bed_days} day${adm.bed_days > 1 ? 's' : ''})` : ''}</Row>
            <Row label="Reason for admission">{adm.reason}</Row>
            <Row label="Attending doctor">{doctor} ({deptLabel(adm.department)})</Row>
            {adm.condition_at_discharge && <Row label="Condition at discharge">{adm.condition_at_discharge}</Row>}
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Diagnosis</h2>
            {c.provisional_diagnosis && <Row label="Provisional diagnosis">{c.provisional_diagnosis}</Row>}
            <Row label="Final diagnosis"><strong data-testid="discharge-letter-diagnosis">{c.final_diagnosis}</strong></Row>
            <Row label="Summary of stay"><span data-testid="discharge-letter-summary">{adm.discharge_summary}</span></Row>
          </div>

          {tests.length > 0 && (
            <div className="mb-6 no-break">
              <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Investigations & Results</h2>
              <table className="w-full text-sm table-fixed">
                <tbody>
                  {tests.map((t) => (
                    <tr key={t.order_id} className="border-b border-gray-100"><td className="py-1 pr-2 w-1/3 text-gray-800">{t.item_name}</td><td className="py-1 text-gray-700 break-words [overflow-wrap:anywhere]">{t.result || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Medicines to take at home</h2>
            {takeHome.length === 0 ? <p className="text-sm text-gray-500">No take-home medicines prescribed.</p> : (
              <table className="w-full text-sm table-fixed" data-testid="discharge-letter-medicines">
                <thead><tr className="text-left text-gray-600 border-b border-gray-300"><th className="py-1 w-2/5">Medicine</th><th className="py-1 w-16">Qty</th><th className="py-1">How to take</th></tr></thead>
                <tbody>
                  {takeHome.map((p) => (
                    <tr key={p.rx_id} className="border-b border-gray-100"><td className="py-1 text-gray-900 break-words">{p.item_name}</td><td className="py-1">{p.quantity}</td><td className="py-1 text-gray-700 break-words [overflow-wrap:anywhere]">{p.instructions || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
            {stayMeds.length > 0 && <p className="text-xs text-gray-500 mt-2">Given in hospital: {stayMeds.map((p) => p.item_name).join(', ')}</p>}
          </div>

          <div className="mb-8 no-break border-2 border-gray-800 rounded-lg p-4">
            <h2 className="text-sm font-bold uppercase text-gray-700 mb-1">Follow-up</h2>
            <p className="text-lg font-bold text-gray-900" data-testid="discharge-letter-followup">{adm.follow_up_date ? `Return on ${fmtDate(adm.follow_up_date)}` : 'Return if symptoms worsen or as advised'}</p>
            {adm.follow_up_notes && <p className="text-sm text-gray-700 whitespace-pre-line mt-1">{adm.follow_up_notes}</p>}
          </div>

          <div className="grid grid-cols-2 gap-8 text-sm text-gray-700 pt-6">
            <div><div className="border-t border-gray-500 pt-1">Doctor: {doctor}</div><div className="text-xs text-gray-500">Signature & stamp</div></div>
            <div><div className="border-t border-gray-500 pt-1">Date: {fmtDate(adm.discharged_at)}</div><div className="text-xs text-gray-500">{visit.hospital_name}</div></div>
          </div>
        </div>
      </div>
    </>
  );
}
